import { storeToRefs } from "pinia";
import { useTasksStore } from "@/stores/v2";
import { computed, ComputedRef, onMounted, ref, watch } from "vue";
import { isFishReconciliationTask } from "@/stores/v2/tasks/utils";
import { Tank, TankGroup } from "@/types/v2";
import { useRouter, useRoute } from "vue-router";
import { TasksService } from "@/services/v2/tasks";
import { normalizeRouteParam } from "@/utils/v2/routes";
import { useTask } from "@/composables/tasks/useTask";

export function useFishReconciliation() {
  const router = useRouter();
  const route = useRoute();
  const tasksStore = useTasksStore();
  const { activeTask } = storeToRefs(tasksStore);
  const { startObserver } = useTask();

  const activeTank = ref<Tank | null>(null);
  const activeGroup = ref<TankGroup | null>(null);
  const isLoading = ref(true);
  const error = ref('');

  const tanks: ComputedRef<Tank[]> = computed(() => {
    if (!activeTask.value || !isFishReconciliationTask(activeTask.value)) return [];
    return activeTask.value.details.tanks.map(tank => ({
      ...tank,
      status: tank.status || "pending"
    }));
  });

  const findTankByGroupKey = (groupKey: string): Tank | null => {
    for (const tank of tanks.value) {
      if (tank.groups?.some(g => g.key === groupKey)) {
        return tank;
      }
    }
    return null;
  };

  const initTask = async () => {
    isLoading.value = true;
    error.value = '';

    const taskKey = normalizeRouteParam(route.params.key);
    if (!taskKey) {
      error.value = 'Invalid task key';
      isLoading.value = false;
      return;
    }

    try {
      tasksStore.resetActiveTaskState();
      const task = await TasksService.startTask(taskKey);
      if (!task) {
        error.value = 'Task not found';
        isLoading.value = false;
        return;
      }
      await tasksStore.setActiveTask(task);
      startObserver();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load task';
    } finally {
      isLoading.value = false;
    }
  };

  const ensureTaskLoaded = async () => {
    if (activeTask.value && tanks.value.length > 0) return true;
    await initTask();
    return !error.value;
  };

  const appendStatus = () => {
    if (!activeTask.value || !isFishReconciliationTask(activeTask.value)) return;

    activeTask.value.details.tanks.forEach(tank => {
      if (!tank.status || tank.status === '') {
        tank.status = 'pending';
      }
    });
  };

  const setActiveTankByKey = (key: string) => {
    activeTank.value = tanks.value.find(t => t.key === key) || null;
  };

  const setActiveGroup = (group: TankGroup) => {
    activeGroup.value = group;
  };

  const updateTankStatus = (tankKey: string, status: string) => {
    if (!activeTask.value || !isFishReconciliationTask(activeTask.value)) return;

    const tank = activeTask.value.details.tanks.find(t => t.key === tankKey);
    if (tank) tank.status = status;
  };

  const updateGroupProgressData = (data: object) => {
    if (!activeTask.value || !isFishReconciliationTask(activeTask.value)) return;
    if (!activeTank.value || !activeGroup.value) return;

    const tank = activeTask.value.details.tanks.find(t => t.key === activeTank.value?.key);
    if (!tank?.groups) return;

    const group = tank.groups.find(g => g.key === activeGroup.value?.key);
    if (!group) return;

    if (!group.progressData || !Array.isArray(group.progressData)) {
      group.progressData = [];
    }

    group.progressData.push(data);
    tank.status = "completed";
  };

  const navigateToTankView = (tankKey: string) => {
    setActiveTankByKey(tankKey);
    router.push({
      name: "FishReconciliationTankView",
      params: {
        key: route.params.key,
        tankKey: tankKey
      }
    });
  };

  const navigateToGroupView = (tankKey: string, groupKey: string) => {
    const tank = tanks.value.find(t => t.key === tankKey);
    if (tank?.groups) {
      const group = tank.groups.find(g => g.key === groupKey);
      if (group) {
        setActiveTankByKey(tankKey);
        setActiveGroup(group);
        router.push({
          name: "FishReconciliationGroupView",
          params: {
            key: route.params.key,
            groupKey: groupKey
          }
        });
      }
    }
  };

  const navigateToOverview = () => {
    router.push({
      name: "FishReconciliationOverview",
      params: { key: route.params.key }
    });
  };

  const initFromRoute = async () => {
    if (route.name === "FishReconciliationOverview" || !activeTask.value) {
      await initTask();
    }

    if (route.name === "FishReconciliationTankView" && route.params.tankKey) {
      setActiveTankByKey(route.params.tankKey.toString());
    } else if (route.name === "FishReconciliationGroupView" && route.params.groupKey) {
      const groupKey = route.params.groupKey.toString();
      const tank = findTankByGroupKey(groupKey);

      if (tank) {
        setActiveTankByKey(tank.key);
        const group = tank.groups?.find(g => g.key === groupKey);
        if (group) setActiveGroup(group);
      }
    }

    appendStatus();
  };

  watch(() => activeTask.value, (newTask) => {
    if (newTask && isFishReconciliationTask(newTask)) {
      appendStatus();
    }
  });

  watch(() => route.params, () => initFromRoute());

  onMounted(() => initFromRoute());

  return {
    activeTask,
    tanks,
    activeTank,
    activeGroup,
    isLoading,
    error,
    setActiveTankByKey,
    setActiveGroup,
    updateTankStatus,
    updateGroupProgressData,
    navigateToTankView,
    navigateToGroupView,
    navigateToOverview,
    initTask,
    ensureTaskLoaded,
  };
}
